import FeedbackInterceptMapping from './FeedbackInterceptMapping';
import fallbackData from './surveys-fallback.json';
import { minimatch } from 'minimatch';

const NULL_SURVEY: FeedbackInterceptMapping = {
  pageGlob: [],
  title: 'No Survey',
  url: '',
};

/**
 * Find the matching survey for the given url. Each survey has an array of glob patterns that are used to match the survey to the path provided.
 * Note that the order of the glob patterns is important. The first matching glob pattern will be used to determine the survey.
 * If a glob pattern starts with a !, it is a negative match. This means that if the url matches this glob pattern, it will be removed from the results.
 *
 * @param mappings
 * @param url
 * @returns FeedbackInterceptMapping
 * @see ./path-globbing.md
 */
export const findSurvey = (mappings: FeedbackInterceptMapping[], path: string): FeedbackInterceptMapping => {
  let results: FeedbackInterceptMapping[] = [];
  mappings.map((survey: FeedbackInterceptMapping) => {
    survey.pageGlob.map((glob: string) => {
      if (glob.startsWith('!')) {
        // if the glob starts with a !, it is a negative match
        if (minimatch(path, glob.slice(1))) {
          // remove the result from the results array
          results = results.filter(found => !Object.is(found, survey));
        }
      }
      else {
        // if the glob does not start with a !, it is a positive match
        if (minimatch(path, glob) && !results.includes(survey)) {
          // add the result to the results array if it's not already there
          results.push(survey);
        }
      }
    });
  });

  // if there are results, return the first one
  return results.length > 0 ? results[0] : NULL_SURVEY;
};

const surveyProvider = () => {
  /**
   * Given the path of a page, provide the title and url for the proper survey
   *
   * @param {string} url - The url of the page
   * @returns {Promise<{ title: string, url: string }>} - The title and url of the survey
   */
  return async ({ path }): Promise<FeedbackInterceptMapping> => {
    // Load the survey mappings from the AEM graphQL endpoint
    let surveyData: FeedbackInterceptMapping[];
    try {
      const endpointResponse = await fetch(
        '/content/cq:graphql/feedback-intercept/endpoint.json',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: `{
              feedbackInterceptMappingList(sort: "_path") {
                items {
                  _path
                  pageGlob
                  url
                  title
                }
              }
            }`,
            operationName: null,
          }),
        },
      );

      if (endpointResponse.ok) {
        const responseData = await endpointResponse.json();
        surveyData = responseData.data?.feedbackInterceptMappingList?.items;
      }
      else {
        // eslint-disable-next-line no-console
        console.error(
          'Error fetching survey data from endpoint:',
          endpointResponse.status,
          endpointResponse.statusText,
        );
      }
    }
    catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error caught while fetching survey data:', error);
    }

    if (surveyData) {
      return findSurvey(surveyData, path);
    }
    // return fallback data if the endpoint fails
    return findSurvey(fallbackData, path);
  };
};

export default surveyProvider;
